import {Box, Stack} from "@mui/material";
import React, {forwardRef, useState} from "react";
import {
    SURVEY_MARKER_SEPARATOR,
    SURVEY_REQUIRED_MARKER,
    surveyQuestionMetaText
} from "../../shared/surveyQuestionMarkers.util";
import {
    SurveySubmissionDocumentData,
    SurveySubmissionDocumentImage,
    SurveySubmissionDocumentLine
} from "./surveySubmissionDocument";

const INK = '#1a1a1a';
const BODY_TEXT = '#333333';
const MUTED = '#999999';
const DISABLED = '#bbbbbb';
const REQUIRED = '#d32f2f';
const LINE = '#e0e0e0';

/*
    The sheet is authored in points, not pixels, because it is measured against a physical page.
    Every value below mirrors SurveySubmissionPDFContent one for one, so the printed page and the
    downloaded PDF are the same document rendered by two engines rather than two similar designs.
*/
const INFO_LABEL_WIDTH = '130pt';

export const SURVEY_SUBMISSION_PRINT_STYLE = `
    @page {
        size: A4;
        /* Matches the PDF page padding: 36pt top, 40pt sides, 58pt bottom. */
        margin: 12.7mm 14.1mm 20.5mm;
    }

    @media print {
        body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        .survey-print-root,
        .survey-print-root * {
            box-shadow: none !important;
            text-shadow: none !important;
        }

        .survey-print-block {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .survey-print-heading {
            break-after: avoid;
            page-break-after: avoid;
        }

        .survey-print-page-break {
            break-before: page;
            page-break-before: always;
        }
    }
`;

interface Props {
    data: SurveySubmissionDocumentData
}

const InfoLines = ({lines}: {lines: SurveySubmissionDocumentLine[]}) => (
    <Stack gap={'2pt'}>
        {lines.map(line => (
            <Stack key={`${line.label}-${line.value}`} direction={'row'}>
                <Box sx={{fontSize: '9pt', color: MUTED, width: INFO_LABEL_WIDTH, flexShrink: 0}}>
                    {line.label}
                </Box>
                <Box sx={{fontSize: '9pt', color: BODY_TEXT, minWidth: 0}}>{line.value}</Box>
            </Stack>
        ))}
    </Stack>
);

const MISSING_TEXT_SX = {fontSize: '9pt', color: MUTED, textAlign: 'center'} as const;

/** An upload's thumbnail; the printer waits until it has loaded or failed. */
const AnswerImage = ({image, missingLabel}: {image: SurveySubmissionDocumentImage, missingLabel: string}) => {
    const [failed, setFailed] = useState(false);

    return (
        <Box
            sx={{
                width: '120pt',
                height: '120pt',
                boxSizing: 'border-box',
                border: `1px solid ${LINE}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: failed ? '8pt' : 0,
            }}
        >
            {failed
                ? <Box sx={MISSING_TEXT_SX}>{missingLabel}</Box>
                : (
                    <Box
                        component={'img'}
                        src={image.src}
                        alt={''}
                        onError={() => setFailed(true)}
                        sx={{display: 'block', width: '100%', height: '100%', objectFit: 'cover'}}
                    />
                )}
        </Box>
    );
};

const AnswerSignature = ({image, missingLabel}: {image: SurveySubmissionDocumentImage, missingLabel: string}) => {
    const [failed, setFailed] = useState(false);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '200pt',
                height: '75pt',
                mt: '6pt',
                p: '6pt',
                boxSizing: 'border-box',
                border: `1px solid ${LINE}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box sx={{position: 'absolute', left: '12pt', right: '12pt', bottom: '15pt', borderBottom: `1px solid ${LINE}`}}/>
            {failed
                ? <Box sx={{...MISSING_TEXT_SX, position: 'relative'}}>{missingLabel}</Box>
                : (
                    <Box
                        component={'img'}
                        src={image.src}
                        alt={''}
                        onError={() => setFailed(true)}
                        sx={{position: 'relative', display: 'block', width: '100%', height: '100%', objectFit: 'contain'}}
                    />
                )}
        </Box>
    );
};

const SectionTitle = ({children}: {children: React.ReactNode}) => (
    <Box
        className={'survey-print-heading'}
        sx={{fontSize: '12pt', fontWeight: 700, color: INK, mb: '8pt'}}
    >
        {children}
    </Box>
);

/** The exact sheet that goes to the printer: header, guest, answers, review and branding. */
const SurveySubmissionPrintable = forwardRef<HTMLDivElement, Props>(({data}, ref) => (
    <Box
        ref={ref}
        className={'survey-print-root'}
        sx={{
            color: BODY_TEXT,
            fontSize: '10pt',
            lineHeight: 1.4,
            bgcolor: '#fff',
            fontFamily: 'Helvetica, Arial, sans-serif',
        }}
    >
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'} gap={'20pt'}>
            <Stack sx={{flexGrow: 1, flexShrink: 1, minWidth: 0}}>
                <Box
                    sx={{
                        fontSize: '9pt',
                        letterSpacing: '1pt',
                        textTransform: 'uppercase',
                        color: MUTED,
                        mb: '4pt',
                    }}
                >
                    {data.kicker}
                </Box>

                <Box sx={{fontSize: '18pt', color: INK, lineHeight: 1.2, mb: '2pt'}}>
                    {data.surveyName}
                </Box>

                {!!data.organizationName && (
                    <Box sx={{fontSize: '11pt', color: BODY_TEXT, mb: '10pt'}}>
                        {data.organizationName}
                    </Box>
                )}

                <InfoLines lines={data.infoLines}/>
            </Stack>

            {!!(data.logoDataUrl || data.logoUrl) && (
                <Box
                    component={'img'}
                    src={data.logoDataUrl || data.logoUrl}
                    alt={''}
                    sx={{
                        flexShrink: 0,
                        maxWidth: '150pt',
                        maxHeight: '54pt',
                        objectFit: 'contain',
                    }}
                />
            )}
        </Stack>

        <Box sx={{borderBottom: `1px solid ${LINE}`, mt: '14pt', mb: '16pt'}}/>

        {!!data.guest && (
            <Box sx={{mb: '16pt'}}>
                <SectionTitle>{data.guest.title}</SectionTitle>

                <Box sx={{fontSize: '14pt', color: INK, mb: '3pt'}}>{data.guest.name}</Box>

                {!!data.guest.chips.length && (
                    <Box sx={{fontSize: '9pt', color: MUTED, mb: '10pt'}}>
                        {data.guest.chips.join('   ·   ')}
                    </Box>
                )}

                {!!data.guest.preferences && (
                    <Stack direction={'row'} sx={{mb: '6pt'}}>
                        <Box sx={{fontSize: '9pt', color: MUTED, width: INFO_LABEL_WIDTH, flexShrink: 0}}>
                            {data.guest.preferencesLabel}
                        </Box>
                        <Box sx={{fontSize: '10pt', color: INK}}>{data.guest.preferences}</Box>
                    </Stack>
                )}

                {data.guest.rows.map(row => (
                    <Stack
                        key={`${row.label}-${row.value}`}
                        className={'survey-print-block'}
                        direction={'row'}
                        sx={{mb: '6pt'}}
                    >
                        <Box sx={{fontSize: '9pt', color: MUTED, width: INFO_LABEL_WIDTH, flexShrink: 0}}>
                            {row.label}
                        </Box>

                        <Box sx={{minWidth: 0}}>
                            <Box sx={{fontSize: '10pt', color: INK}}>{row.value}</Box>
                            {!!row.detail && (
                                <Box sx={{fontSize: '9pt', color: MUTED, mt: '1pt'}}>{row.detail}</Box>
                            )}
                        </Box>
                    </Stack>
                ))}
            </Box>
        )}

        {!!data.review && (
            <Box sx={{mb: '16pt'}}>
                <SectionTitle>{data.review.title}</SectionTitle>

                <Stack direction={'row'} className={'survey-print-block'} sx={{mb: '6pt'}}>
                    <Box sx={{fontSize: '9pt', color: MUTED, width: INFO_LABEL_WIDTH, flexShrink: 0}}>
                        {data.review.stateLabel}
                    </Box>

                    <Box sx={{minWidth: 0}}>
                        <Box sx={{fontSize: '10pt', color: INK}}>{data.review.stateValue}</Box>
                        {!!data.review.reviewedLine && (
                            <Box sx={{fontSize: '9pt', color: MUTED, mt: '1pt'}}>
                                {data.review.reviewedLine}
                            </Box>
                        )}
                    </Box>
                </Stack>

                <Stack direction={'row'} sx={{mb: '6pt'}}>
                    <Box sx={{fontSize: '9pt', color: MUTED, width: INFO_LABEL_WIDTH, flexShrink: 0}}>
                        {data.review.notesLabel}
                    </Box>

                    {data.review.notes
                        ? (
                            <Box
                                sx={{
                                    fontSize: '10pt',
                                    color: BODY_TEXT,
                                    lineHeight: 1.4,
                                    whiteSpace: 'pre-wrap',
                                    minWidth: 0,
                                }}
                            >
                                {data.review.notes}
                            </Box>
                        )
                        : (
                            <Box sx={{fontSize: '10pt', color: MUTED}}>
                                {data.review.emptyNotesLabel}
                            </Box>
                        )}
                </Stack>
            </Box>
        )}

        {/* Header, guest and review own the first page; the answers start their own. */}
        <Box className={'survey-print-page-break'} sx={{mb: '16pt'}}>
            <SectionTitle>{data.answersTitle}</SectionTitle>

            {data.answers.length === 0
                ? <Box sx={{fontSize: '10pt', color: MUTED}}>{data.emptyAnswersLabel}</Box>
                : data.answers.map((answer, index) => {
                    const meta = surveyQuestionMetaText(
                        answer.typeLabel,
                        answer.sensitiveLabel,
                        answer.hiddenLabel
                    );

                    return (
                        <Box key={index} className={'survey-print-block'} sx={{mb: '12pt'}}>
                            <Box sx={{fontSize: '9.5pt', color: MUTED, mb: '3pt'}}>
                                {answer.question}
                                {answer.required && (
                                    <Box component={'span'} sx={{color: REQUIRED}}>
                                        {` ${SURVEY_REQUIRED_MARKER}`}
                                    </Box>
                                )}
                                {!!meta && (
                                    <Box component={'span'} sx={{fontSize: '8pt', color: MUTED}}>
                                        {`${SURVEY_MARKER_SEPARATOR}${meta}`}
                                    </Box>
                                )}
                            </Box>
                            {!!answer.answer && (
                                <Box
                                    sx={{
                                        fontSize: '11pt',
                                        color: answer.empty ? DISABLED : answer.hidden ? MUTED : INK,
                                        lineHeight: 1.4,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {answer.answer}
                                </Box>
                            )}
                            {!!answer.images.length && (
                                <Stack direction={'row'} flexWrap={'wrap'} gap={'8pt'} sx={{mt: '6pt'}}>
                                    {answer.images.map((image, imageIndex) => (
                                        <AnswerImage key={imageIndex} image={image} missingLabel={data.imageMissingLabel}/>
                                    ))}
                                </Stack>
                            )}
                            {!!answer.signature && (
                                <AnswerSignature image={answer.signature} missingLabel={data.signatureMissingLabel}/>
                            )}
                        </Box>
                    );
                })}
        </Box>

        {!!data.footer && (
            <Box sx={{fontSize: '8pt', color: MUTED, mt: '10pt'}}>{data.footer}</Box>
        )}

        <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={'20pt'}
            sx={{borderTop: `1px solid ${LINE}`, pt: '8pt', mt: '16pt'}}
        >
            {!!data.branding && (
                <Stack direction={'row'} alignItems={'center'} gap={'4pt'}>
                    <Box sx={{fontSize: '8.5pt', color: MUTED}}>{data.branding.label}</Box>
                    {!!data.branding.markDataUrl && (
                        <Box
                            component={'img'}
                            src={data.branding.markDataUrl}
                            alt={''}
                            sx={{display: 'block', width: '9pt', height: '9pt', flexShrink: 0}}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    </Box>
));

SurveySubmissionPrintable.displayName = 'SurveySubmissionPrintable';

export default SurveySubmissionPrintable;
